/**
 * Geometria e suporte da construção em grade (doc 03): chaves de ocupação, caixas de colisão,
 * contato com o terreno e o grafo de suporte com vão máximo. Puro e determinístico (só aritmética):
 * o servidor valida e derruba; o cliente prevê a prévia e quantas peças caem ao desmontar.
 */
import { segmentHitsCylinder, terrainBlocks } from "./combat.ts";
import type { Cylinder } from "./movement.ts";

export type PieceKind = "alicerce" | "piso" | "parede" | "porta" | "rampa" | "telhado" | "estacada";

export const GRID = {
  cell: 2,
  level: 1,
  wallHeight: 2,
  wallThickness: 0.2,
  stakeThickness: 0.3,
  floorThickness: 0.2,
  foundationDepth: 2,
  doorWidth: 1,
  maxSpan: 4,
} as const;

/** Posição na grade: célula (gx, gz), nível gy (m) e giro (0–3, ×90°). */
export interface GridPos {
  gx: number;
  gy: number;
  gz: number;
  rot: number;
}

export interface PieceInfo extends GridPos {
  id: string;
  kind: PieceKind;
  open?: boolean;
}

/** Caixa alinhada aos eixos. `ramp`: o topo sobe de `minY` a `maxY` na direção (`axis`, `dir`). */
export interface Box {
  minX: number;
  maxX: number;
  minY: number;
  maxY: number;
  minZ: number;
  maxZ: number;
  ramp?: { axis: "x" | "z"; dir: 1 | -1 };
  /**
   * Teto suspenso (a laje da Caverna): o corpo pisa em cima e bate a cabeça embaixo como em qualquer
   * caixa, mas não é piso onde ataques e perigos caem (`floorHeight`): quem está embaixo continua no chão.
   */
  ceiling?: boolean;
}

export const isEdgeKind = (kind: PieceKind): boolean => kind === "parede" || kind === "porta" || kind === "estacada";
/** Paredes ao longo de x nos giros pares, ao longo de z nos ímpares. */
export const edgeAxis = (rot: number): "x" | "z" => (((rot % 4) + 4) % 4) % 2 === 0 ? "x" : "z";
/** Para onde aponta o giro (como o yaw dos corpos: 0 olha para −z, 1 para −x, 2 para +z, 3 para +x). */
export function rotDir(rot: number): { x: number; z: number } {
  switch (((rot % 4) + 4) % 4) {
    case 0:
      return { x: 0, z: -1 };
    case 1:
      return { x: -1, z: 0 };
    case 2:
      return { x: 0, z: 1 };
    default:
      return { x: 1, z: 0 };
  }
}

/**
 * Chaves de ocupação: centro `c:gx:gy:gz` (a rampa ocupa dois níveis) e borda `x|z:gx:gy:gz`
 * (paredes ocupam os dois níveis da altura de 2 m). A borda é única por aresta da grade.
 */
export function occupancy(kind: PieceKind, p: GridPos): string[] {
  if (isEdgeKind(kind)) {
    const axis = edgeAxis(p.rot);
    return [`${axis}:${p.gx}:${p.gy}:${p.gz}`, `${axis}:${p.gx}:${p.gy + 1}:${p.gz}`];
  }
  if (kind === "rampa") return [`c:${p.gx}:${p.gy}:${p.gz}`, `c:${p.gx}:${p.gy + 1}:${p.gz}`];
  return [`c:${p.gx}:${p.gy}:${p.gz}`];
}

/** Centro no mundo e yaw (para desenhar). */
export function pieceCenter(kind: PieceKind, p: GridPos): { x: number; y: number; z: number; yaw: number } {
  const yaw = (((p.rot % 4) + 4) % 4) * (Math.PI / 2);
  if (isEdgeKind(kind)) {
    const axis = edgeAxis(p.rot);
    return axis === "x" ? { x: p.gx * GRID.cell + GRID.cell / 2, y: p.gy, z: p.gz * GRID.cell, yaw } : { x: p.gx * GRID.cell, y: p.gy, z: p.gz * GRID.cell + GRID.cell / 2, yaw };
  }
  return { x: p.gx * GRID.cell + GRID.cell / 2, y: p.gy, z: p.gz * GRID.cell + GRID.cell / 2, yaw };
}

/** Caixa de uma faixa da aresta (de `a` a `b` metros ao longo dela). */
function edgeBox(p: GridPos, a: number, b: number, thickness: number): Box {
  const half = thickness / 2;
  if (edgeAxis(p.rot) === "x") {
    const z0 = p.gz * GRID.cell;
    return { minX: p.gx * GRID.cell + a, maxX: p.gx * GRID.cell + b, minY: p.gy, maxY: p.gy + GRID.wallHeight, minZ: z0 - half, maxZ: z0 + half };
  }
  const x0 = p.gx * GRID.cell;
  return { minX: x0 - half, maxX: x0 + half, minY: p.gy, maxY: p.gy + GRID.wallHeight, minZ: p.gz * GRID.cell + a, maxZ: p.gz * GRID.cell + b };
}

/**
 * Caixas de colisão da peça. Parede: a aresta inteira; porta: dois batentes de 0,5 m e, fechada, a
 * folha no vão do meio; piso e telhado: laje fina; alicerce: bloco que desce 2 m; rampa: caixa com
 * topo inclinado (sobe 2 m na direção do giro).
 */
export function pieceBoxes(kind: PieceKind, p: GridPos, open = false): Box[] {
  const x0 = p.gx * GRID.cell;
  const z0 = p.gz * GRID.cell;
  const cellBox = (minY: number, maxY: number): Box => ({ minX: x0, maxX: x0 + GRID.cell, minY, maxY, minZ: z0, maxZ: z0 + GRID.cell });
  switch (kind) {
    case "parede":
      return [edgeBox(p, 0, GRID.cell, GRID.wallThickness)];
    case "estacada":
      return [edgeBox(p, 0, GRID.cell, GRID.stakeThickness)];
    case "porta": {
      const side = (GRID.cell - GRID.doorWidth) / 2;
      const boxes = [edgeBox(p, 0, side, GRID.wallThickness), edgeBox(p, GRID.cell - side, GRID.cell, GRID.wallThickness)];
      if (!open) boxes.push(edgeBox(p, side, GRID.cell - side, GRID.wallThickness));
      return boxes;
    }
    case "piso":
      return [cellBox(p.gy - GRID.floorThickness, p.gy)];
    case "telhado":
      return [cellBox(p.gy, p.gy + GRID.floorThickness)];
    case "alicerce":
      return [cellBox(p.gy - GRID.foundationDepth, p.gy)];
    case "rampa": {
      const d = rotDir(p.rot);
      return [{ ...cellBox(p.gy, p.gy + GRID.wallHeight), ramp: { axis: d.x !== 0 ? "x" : "z", dir: (d.x !== 0 ? d.x : d.z) as 1 | -1 } }];
    }
  }
}

/** Topo da caixa no ponto (a rampa sobe linearmente; as demais são planas). */
export function boxTopAt(box: Box, x: number, z: number): number {
  if (!box.ramp) return box.maxY;
  const span = box.ramp.axis === "x" ? box.maxX - box.minX : box.maxZ - box.minZ;
  const along = box.ramp.axis === "x" ? (box.ramp.dir > 0 ? x - box.minX : box.maxX - x) : box.ramp.dir > 0 ? z - box.minZ : box.maxZ - z;
  const k = Math.max(0, Math.min(1, along / span));
  return box.minY + (box.maxY - box.minY) * k;
}

/** Pontos de apoio da peça no chão (cantos e meio da célula, ou as pontas e o meio da aresta). */
function footprint(kind: PieceKind, p: GridPos): { x: number; z: number }[] {
  const x0 = p.gx * GRID.cell;
  const z0 = p.gz * GRID.cell;
  const c = GRID.cell;
  if (isEdgeKind(kind)) {
    return edgeAxis(p.rot) === "x"
      ? [{ x: x0 + 0.1, z: z0 }, { x: x0 + c / 2, z: z0 }, { x: x0 + c - 0.1, z: z0 }]
      : [{ x: x0, z: z0 + 0.1 }, { x: x0, z: z0 + c / 2 }, { x: x0, z: z0 + c - 0.1 }];
  }
  return [
    { x: x0 + 0.1, z: z0 + 0.1 },
    { x: x0 + c - 0.1, z: z0 + 0.1 },
    { x: x0 + 0.1, z: z0 + c - 0.1 },
    { x: x0 + c - 0.1, z: z0 + c - 0.1 },
    { x: x0 + c / 2, z: z0 + c / 2 },
  ];
}

/** Menor e maior altura do terreno sob a peça. */
export function terrainUnder(kind: PieceKind, p: GridPos, height: (x: number, z: number) => number): { min: number; max: number } {
  let min = Infinity;
  let max = -Infinity;
  for (const f of footprint(kind, p)) {
    const h = height(f.x, f.z);
    if (h < min) min = h;
    if (h > max) max = h;
  }
  return { min, max };
}

/**
 * A peça encosta no terreno (apoio direto)? Alicerce: o terreno está entre o topo e 2 m abaixo
 * dele (é ele que vence o declive). Telhado nunca. As demais: o terreno passa no máximo 0,5 m da
 * base (a laje afunda um pouco no chão) e não cai mais de 0,6 m sob ela. A janela de 1,1 m garante
 * um nível inteiro válido em qualquer chão plano (o encaixe arredonda a altura).
 */
export function touchesGround(kind: PieceKind, p: GridPos, height: (x: number, z: number) => number): boolean {
  if (kind === "telhado") return false;
  const t = terrainUnder(kind, p, height);
  if (kind === "alicerce") return t.max <= p.gy + 0.5 && t.min >= p.gy - GRID.foundationDepth - 0.05;
  return t.max <= p.gy + 0.5 && t.min >= p.gy - 0.6;
}

/** A peça fica enterrada (o terreno passa do topo dela)? Não se constrói assim. */
export function buried(kind: PieceKind, p: GridPos, height: (x: number, z: number) => number): boolean {
  const t = terrainUnder(kind, p, height);
  // Parede: até metade enterrada; rampa: até o topo; lajes: o terreno passa no máximo 0,5 m do topo
  // (a mesma folga do apoio, para todo chão plano ter um nível válido).
  const limit = isEdgeKind(kind) ? p.gy + GRID.wallHeight - 1 : kind === "rampa" ? p.gy + GRID.wallHeight : p.gy + 0.5;
  return t.max > limit || (kind !== "alicerce" && t.min > p.gy + 0.5);
}

const cellKey = (gx: number, gy: number, gz: number) => `c:${gx}:${gy}:${gz}`;
const edgeKey = (axis: "x" | "z", gx: number, gy: number, gz: number) => `${axis}:${gx}:${gy}:${gz}`;

/** As 4 bordas da célula no nível `gy`. */
function cellEdges(gx: number, gy: number, gz: number): string[] {
  return [edgeKey("x", gx, gy, gz), edgeKey("x", gx, gy, gz + 1), edgeKey("z", gx, gy, gz), edgeKey("z", gx + 1, gy, gz)];
}

/** As 2 células de cada lado da borda no nível `gy`. */
function edgeCells(axis: "x" | "z", gx: number, gy: number, gz: number): string[] {
  return axis === "x" ? [cellKey(gx, gy, gz - 1), cellKey(gx, gy, gz)] : [cellKey(gx - 1, gy, gz), cellKey(gx, gy, gz)];
}

/**
 * Vão de cada peça apoiada (doc 03, Suporte): 0 para as que encostam no terreno e para paredes
 * sobre algo apoiado (apoio vertical); +1 a cada célula de piso, rampa ou telhado na horizontal a
 * partir do apoio. Peças fora do mapa não têm caminho até o chão, ou passam de `maxSpan`.
 *
 * Arestas (quem apoia → quem é apoiado): chão → peça; piso/alicerce/rampa → parede na borda dele,
 * no mesmo nível; parede → piso, telhado ou rampa no topo dela (nível + 2) e parede de cima; piso
 * ↔ piso vizinho no mesmo nível (+1); rampa ↔ célula de trás (mesmo nível) e da frente (nível + 2).
 */
export function supportSpans(pieces: readonly PieceInfo[], grounded: (p: PieceInfo) => boolean, maxSpan: number = GRID.maxSpan): Map<string, number> {
  const byKey = new Map<string, PieceInfo>();
  for (const p of pieces) for (const k of occupancy(p.kind, p)) byKey.set(k, p);
  const span = new Map<string, number>();
  // 0-1 BFS: arestas de custo 0 vão para a frente da fila; de custo 1, para o fim.
  const deque: { p: PieceInfo; s: number }[] = [];
  let head = 0;
  const push = (p: PieceInfo | undefined, s: number, front: boolean) => {
    if (!p || s > maxSpan) return;
    const cur = span.get(p.id);
    if (cur !== undefined && cur <= s) return;
    span.set(p.id, s);
    if (front && head > 0) deque[--head] = { p, s };
    else if (front) deque.unshift({ p, s });
    else deque.push({ p, s });
  };
  for (const p of pieces) if (grounded(p)) push(p, 0, false);
  while (head < deque.length) {
    const { p, s } = deque[head++] as { p: PieceInfo; s: number };
    if (span.get(p.id) !== s) continue;
    if (isEdgeKind(p.kind)) {
      const axis = edgeAxis(p.rot);
      // Parede de cima e o que repousa no topo dela.
      push(byKey.get(edgeKey(axis, p.gx, p.gy + GRID.wallHeight, p.gz)), s, true);
      for (const k of edgeCells(axis, p.gx, p.gy + GRID.wallHeight, p.gz)) {
        const top = byKey.get(k);
        if (top && top.gy === p.gy + GRID.wallHeight) push(top, s, true);
      }
      continue;
    }
    // Centro: paredes nas bordas (no mesmo nível) e vizinhos de piso (+1).
    if (p.kind !== "telhado") for (const k of cellEdges(p.gx, p.gy, p.gz)) push(byKey.get(k), s, true);
    const neighbors: [number, number][] = [
      [p.gx + 1, p.gz],
      [p.gx - 1, p.gz],
      [p.gx, p.gz + 1],
      [p.gx, p.gz - 1],
    ];
    for (const [nx, nz] of neighbors) {
      const n = byKey.get(cellKey(nx, p.gy, nz));
      if (n && n.gy === p.gy && !isEdgeKind(n.kind)) push(n, s + 1, false);
    }
    if (p.kind === "rampa") {
      const d = rotDir(p.rot);
      const front = byKey.get(cellKey(p.gx + d.x, p.gy + GRID.wallHeight, p.gz + d.z));
      if (front && front.gy === p.gy + GRID.wallHeight) push(front, s + 1, false);
    }
    // Rampa vizinha cuja frente sobe até este piso.
    for (const [nx, nz] of neighbors) {
      const r = byKey.get(cellKey(nx, p.gy - GRID.wallHeight, nz));
      if (r?.kind !== "rampa" || r.gy !== p.gy - GRID.wallHeight) continue;
      const d = rotDir(r.rot);
      if (r.gx + d.x === p.gx && r.gz + d.z === p.gz) push(r, s + 1, false);
    }
  }
  return span;
}

/**
 * O que cai se as peças `removed` saírem: as que perdem o caminho até o chão (ou passam do vão),
 * com o salto (distância na grade) a partir da peça removida mais perto, para a cascata em ondas.
 */
export function cascade(pieces: readonly PieceInfo[], removed: ReadonlySet<string>, grounded: (p: PieceInfo) => boolean): { id: string; hop: number }[] {
  const left = pieces.filter((p) => !removed.has(p.id));
  const spans = supportSpans(left, grounded);
  const falling = left.filter((p) => !spans.has(p.id));
  if (falling.length === 0) return [];
  const origins = pieces.filter((p) => removed.has(p.id));
  return falling
    .map((p) => {
      let hop = Infinity;
      for (const o of origins) hop = Math.min(hop, Math.abs(p.gx - o.gx) + Math.abs(p.gz - o.gz) + Math.abs(p.gy - o.gy) / GRID.wallHeight);
      return { id: p.id, hop: Math.max(1, Math.round(hop)) };
    })
    .sort((a, b) => a.hop - b.hop || (a.id < b.id ? -1 : 1));
}

/**
 * Encaixe na grade a partir do ponto mirado: célula sob o ponto; borda mais perto na direção do
 * giro (paredes); nível arredondado ao metro.
 */
export function snapPiece(kind: PieceKind, hit: { x: number; y: number; z: number }, rot: number): GridPos {
  const r = ((rot % 4) + 4) % 4;
  const gy = Math.round(hit.y);
  if (isEdgeKind(kind)) {
    return edgeAxis(r) === "x" ? { gx: Math.floor(hit.x / GRID.cell), gy, gz: Math.round(hit.z / GRID.cell), rot: r } : { gx: Math.round(hit.x / GRID.cell), gy, gz: Math.floor(hit.z / GRID.cell), rot: r };
  }
  return { gx: Math.floor(hit.x / GRID.cell), gy, gz: Math.floor(hit.z / GRID.cell), rot: r };
}

/**
 * Primeiro contato do segmento `a → b` com a caixa (método das faixas): fração em [0, 1] ou
 * `undefined`. Na rampa, o volume é só o que fica sob o topo inclinado (um semiespaço a mais).
 */
export function segmentBoxEntry(a: { x: number; y: number; z: number }, b: { x: number; y: number; z: number }, box: Box): number | undefined {
  let t0 = 0;
  let t1 = 1;
  const axes: [number, number, number, number][] = [
    [a.x, b.x - a.x, box.minX, box.maxX],
    [a.y, b.y - a.y, box.minY, box.maxY],
    [a.z, b.z - a.z, box.minZ, box.maxZ],
  ];
  for (const [o, d, lo, hi] of axes) {
    if (Math.abs(d) < 1e-12) {
      if (o < lo || o > hi) return undefined;
      continue;
    }
    let u0 = (lo - o) / d;
    let u1 = (hi - o) / d;
    if (u0 > u1) [u0, u1] = [u1, u0];
    if (u0 > t0) t0 = u0;
    if (u1 < t1) t1 = u1;
    if (t0 > t1) return undefined;
  }
  if (box.ramp) {
    // Acima do topo é ar: g(p) = y − topo(p) ≤ 0. `boxTopAt` satura fora da caixa, mas na faixa
    // [t0, t1] o segmento está dentro dela, onde g é linear: basta avaliar nas pontas da faixa.
    const at = (t: number) => {
      const x = a.x + (b.x - a.x) * t;
      const z = a.z + (b.z - a.z) * t;
      return a.y + (b.y - a.y) * t - boxTopAt(box, x, z);
    };
    const g0 = at(t0);
    const g1 = at(t1);
    if (g0 > 0 && g1 > 0) return undefined;
    if (g0 > 0) t0 = t0 + ((t1 - t0) * g0) / (g0 - g1);
  }
  return t0;
}

/** Distância do ponto à caixa (0 dentro dela). */
export function boxDistance(box: Box, p: { x: number; y: number; z: number }): number {
  const dx = p.x < box.minX ? box.minX - p.x : p.x > box.maxX ? p.x - box.maxX : 0;
  const dy = p.y < box.minY ? box.minY - p.y : p.y > box.maxY ? p.y - box.maxY : 0;
  const dz = p.z < box.minZ ? box.minZ - p.z : p.z > box.maxZ ? p.z - box.maxZ : 0;
  return Math.sqrt(dx * dx + dy * dy + dz * dz);
}

/** Distância do ponto (o olho de quem interage) à parte mais perto da peça, em 3D. */
export function pieceDistance(kind: PieceKind, p: GridPos, point: { x: number; y: number; z: number }, open = false): number {
  let best = Infinity;
  for (const b of pieceBoxes(kind, p, open)) best = Math.min(best, boxDistance(b, point));
  return best;
}

/**
 * Peça de centro (menos o alicerce) com a base abaixo do mar, sobre água funda: na água, só o
 * alicerce entra (e paredes sobre algo, pelo apoio). Um piso acima do mar, apoiado, pode.
 */
export function inDeepWater(kind: PieceKind, p: GridPos, height: (x: number, z: number) => number, seaLevel: number): boolean {
  if (isEdgeKind(kind) || kind === "alicerce") return false;
  const c = pieceCenter(kind, p);
  if (height(c.x, c.z) >= seaLevel - 0.5) return false;
  let bottom = Infinity;
  for (const b of pieceBoxes(kind, p)) bottom = Math.min(bottom, b.minY);
  return bottom < seaLevel;
}

/** O que bloqueia a visão de quem constrói: terreno, troncos, rochas, estações e peças. */
export interface SightWorld {
  height(x: number, z: number): number;
  cylinders(x: number, z: number, radius: number, out: Cylinder[]): Cylinder[];
  boxes?(x: number, z: number, radius: number, out: Box[]): Box[];
}

const sightCylinders: Cylinder[] = [];
const sightBoxes: Box[] = [];

/** Algo cruza o segmento `a → b` (terreno, cilindro ou caixa)? */
export function segmentBlocked(world: SightWorld, a: { x: number; y: number; z: number }, b: { x: number; y: number; z: number }): boolean {
  if (terrainBlocks((x, z) => world.height(x, z), a, b)) return true;
  const mx = (a.x + b.x) / 2;
  const mz = (a.z + b.z) / 2;
  const half = Math.sqrt((b.x - a.x) * (b.x - a.x) + (b.z - a.z) * (b.z - a.z)) / 2;
  sightCylinders.length = 0;
  for (const c of world.cylinders(mx, mz, half + 1, sightCylinders)) if (segmentHitsCylinder(a, b, c)) return true;
  if (!world.boxes) return false;
  sightBoxes.length = 0;
  for (const box of world.boxes(mx, mz, half + 1, sightBoxes)) if (segmentBoxEntry(a, b, box) !== undefined) return true;
  return false;
}

/**
 * Pontos da peça nova que quem constrói precisa enxergar (basta um): o topo da laje (centro e
 * perto do meio de cada lado) ou a face da parede (três alturas em três pontos da aresta). Nunca
 * abaixo do terreno. Impede construir do outro lado de uma parede fechada.
 */
export function sightPoints(kind: PieceKind, p: GridPos, height: (x: number, z: number) => number): { x: number; y: number; z: number }[] {
  const out: { x: number; y: number; z: number }[] = [];
  const add = (x: number, y: number, z: number) => out.push({ x, y: Math.max(y, height(x, z) + 0.1), z });
  if (isEdgeKind(kind)) {
    const c = pieceCenter(kind, p);
    const along = edgeAxis(p.rot) === "x" ? { x: 1, z: 0 } : { x: 0, z: 1 };
    for (const k of [-0.75, 0, 0.75]) for (const h of [0.4, 1, 1.8]) add(c.x + along.x * k, p.gy + h, c.z + along.z * k);
    return out;
  }
  const [box] = pieceBoxes(kind, p);
  if (!box) return out;
  const x0 = p.gx * GRID.cell;
  const z0 = p.gz * GRID.cell;
  const inset = 0.3;
  const points: [number, number][] = [
    [x0 + GRID.cell / 2, z0 + GRID.cell / 2],
    [x0 + inset, z0 + GRID.cell / 2],
    [x0 + GRID.cell - inset, z0 + GRID.cell / 2],
    [x0 + GRID.cell / 2, z0 + inset],
    [x0 + GRID.cell / 2, z0 + GRID.cell - inset],
  ];
  for (const [x, z] of points) add(x, boxTopAt(box, x, z) + 0.05, z);
  return out;
}

/** Quem constrói (do olho) enxerga algum ponto da peça nova? */
export function canSeePiece(world: SightWorld, kind: PieceKind, p: GridPos, eye: { x: number; y: number; z: number }): boolean {
  return sightPoints(kind, p, (x, z) => world.height(x, z)).some((q) => !segmentBlocked(world, eye, q));
}

export interface BuildWorld extends SightWorld {
  seaLevel: number;
}

/**
 * Motivo para não construir (o mesmo no servidor e na prévia), ou `undefined`: alcance de 8 m do
 * olho, limite de peças, ocupação, terreno, água funda, visão e apoio. Corpos no caminho ficam só
 * no servidor (a prévia não conhece as criaturas).
 */
export function buildRefusal(
  kind: PieceKind,
  p: GridPos,
  eye: { x: number; y: number; z: number },
  pieces: readonly PieceInfo[],
  world: BuildWorld,
  rules: { reach: number; maxPieces: number },
): string | undefined {
  const center = pieceCenter(kind, p);
  const height = (x: number, z: number) => world.height(x, z);
  const dx = center.x - eye.x;
  const dy = center.y + 1 - eye.y;
  const dz = center.z - eye.z;
  if (Math.sqrt(dx * dx + dy * dy + dz * dz) > rules.reach) return "longe demais";
  if (pieces.length >= rules.maxPieces) return "limite de peças da partida";
  const keys = occupancy(kind, p);
  if (pieces.some((q) => occupancy(q.kind, q).some((k) => keys.includes(k)))) return "lugar ocupado";
  if (buried(kind, p, height)) return "o terreno não deixa";
  if (inDeepWater(kind, p, height, world.seaLevel)) return "na água, só sobre alicerce";
  if (!canSeePiece(world, kind, p, eye)) return "fora da vista";
  const spans = supportSpans([...pieces, { id: "novo", kind, ...p }], (q) => touchesGround(q.kind, q, height));
  if (!spans.has("novo")) return "sem apoio (encoste no chão, num alicerce ou a até 4 células de um apoio)";
  return undefined;
}
